import classNames from 'classnames';
import { useCombobox, useMultipleSelection } from 'downshift';
import { ComponentProps, forwardRef, useEffect, useMemo, useState, useRef } from 'react';
import { Checked, DropDownArrow } from '../../../../assets/images/icons';
import { PlusIconPurple, SearchIconGrey, CloseIconPurple, VectorDown } from '../../../../assets/images/new-icons';
import { CustomCheckbox } from '../../../common';
import cls from './MultiSelectWithCheckboxesAndNestedItems.module.scss';
import * as R from 'ramda';

type SimpleItem = { value: string; label: string; icon?: string };
type ComplexItem = SimpleItem[];
export type MultiSelectWithCheckboxesAndNestedItemsProps = {
    /** list of items to chose from */
    items: SimpleItem[];
    groupedBy?: keyof SimpleItem;
    /**displayed as label above Control */
    title?: string;
    placeholder?: string;
    /** sets an id to the related elements as required by aria listbox rules. Falls back to `title` if none is provided */
    id?: string;
    /** adds a "*"-Indicator to the end of the title that turns red in case of an error*/
    hasMandatoryIndicator?: boolean;
    /** id's of the items that need to be selected initially*/
    initialSelectedValues: SimpleItem[];
    errorMessage?: string;
    onChange?: (selectedItems?: SimpleItem[]) => void;
    noTags?: boolean;
    inputValue: string;
    triggerType?: 'chevron' | 'downArrow' | 'eyeglass' | 'eyeglassGrey';
    /** prop to pass className to Input */
    inputClassName?: string;
    /** prop to pass className to MenuOverlayList */
    molclassname?: string;
    /** prop to pass className to Input */
    inputContainerClassName: string;
    /** prop to pass className to MenuOverlayListItem */
    moliclassname?: string;
    inputStyle: Record<string, unknown>;
    menuOverlayStyle: Record<string, unknown>;
    menuOverlayListItemStyle: Record<string, unknown>;
    disabled?: boolean;
} & ComponentProps<'div'>;
const groupBy = (propName: string, array: SimpleItem[]): ComplexItem => R.valuesIn(R.groupBy(R.prop(propName))(R.sortWith([R.ascend(R.prop(propName))])(array)));

const MultiSelectWithCheckboxesAndNestedItems = (props: MultiSelectWithCheckboxesAndNestedItemsProps) => {
    const {
        title,
        hasMandatoryIndicator,
        id = title?.replace(/ /g, '-'),
        triggerType,
        items,
        initialSelectedValues,
        onChange,
        errorMessage,
        placeholder,
        /* inputValue, */ children,
        className,
        inputClassName,
        groupedBy,
        molclassname,
        moliclassname,
        inputStyle,
        inputContainerClassName,
        menuOverlayStyle,
        menuOverlayListItemStyle,
        noTags,
        disabled=false,
        ...rest
    } = props;
    const [searchFilteredItems, setSearchFilteredItems] = useState(items);
    const [selectedItems, setSelectedItems] = useState<SimpleItem[]>(initialSelectedValues ?? []);
    const grouped = useMemo(() => groupBy(groupedBy ?? '', searchFilteredItems), [searchFilteredItems, groupedBy]);
    const {
        getSelectedItemProps,
        getDropdownProps,
    } = useMultipleSelection<SimpleItem>({
        initialSelectedItems: items?.filter(i => initialSelectedValues?.filter(e => e?.value == i?.value)?.length > 0),
    });
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onChange?.(selectedItems);
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    }, [selectedItems]);

    const { isOpen, getToggleButtonProps, getLabelProps, getMenuProps, getInputProps, getComboboxProps, highlightedIndex, getItemProps, openMenu, selectItem } = useCombobox<SimpleItem | null>({
        items: searchFilteredItems,
        id,
        // itemToString: (item:SimpleItem|null) => item?.label??'',
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;

            switch (type) {
                //clear the input when clicking outside or toggle button
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                case useCombobox.stateChangeTypes.InputBlur:
                    return {
                        ...changes,
                        inputValue: '',
                    };
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    return {
                        ...changes,
                        isOpen: true, // keep the menu open after selection.
                        highlightedIndex: 0, // with the first option highlighted.
                    };
                default:
                    return changes;
            }
        },
        onStateChange: ({ type, selectedItem }) => {
            switch (type) {
                case useCombobox.stateChangeTypes.InputKeyDownEnter:
                case useCombobox.stateChangeTypes.ItemClick:
                    if (selectedItem) {
                        const indexOfItem = selectedItems.findIndex(item => item.value === selectedItem.value);

                        if (indexOfItem > -1) {
                            setSelectedItems([...selectedItems.filter(item => item.value !== selectedItem.value)]);
                        } else {
                            setSelectedItems([...selectedItems, selectedItem]);
                        }
                        selectItem(null);
                    }

                    return {
                        isOpen: true, // keep the menu open after selection.
                        highlightedIndex: 0, // with the first option highlighted.
                    };
                default:
                    break;
            }
        },
        onInputValueChange: ({ inputValue }) => {
            setSearchFilteredItems(inputValue ?
                items?.filter(item => item?.label?.toLowerCase()?.includes(inputValue?.toLowerCase())) : items);
        },
    });

    return (
        <div style={{position: "relative"}}>
            {disabled && <div style={{position: "absolute", height: "100%", width: "100%", zIndex: 1, background: "rgb(235, 235, 228, 0.2)"}}/>}
            <div className={classNames(cls.host, className)} {...rest}>
                <Label {...getLabelProps()} hasMandatoryIndicator={hasMandatoryIndicator}>
                    {title}
                </Label>
                {!noTags && selectedItems?.length > 0 && (
                    <div className={cls.multiSelectSection}  ref={scrollContainerRef}>
                        {selectedItems.map((selectedItem, index) => (
                            <Tag
                                hasCloseButton
                                key={`selected-item${index}`}
                                {...getSelectedItemProps({ selectedItem, index })}
                                onClick={() => {
                                    setSelectedItems([...selectedItems.filter(item => item.value !== selectedItem.value)]);
                                }}
                            >
                                {selectedItem?.icon && <img alt="item-icon" className={cls.itemLeftIcon} src={selectedItem?.icon} />}
                                {selectedItem.label}
                            </Tag>
                        ))}
                    </div>
                )}
                <InputContainer {...getComboboxProps()}
                    triggerType={triggerType} className={inputContainerClassName} isActive={isOpen} hasError={errorMessage != null}>
                    <Input
                        {...getInputProps({
                            placeholder,
                            'aria-invalid': errorMessage != null || undefined,
                            onFocus: () => {
                                if (!isOpen) {
                                    openMenu();
                                }
                            },
                        })}
                        className={inputClassName}
                        style={inputStyle}
                    />
                    <ToggleMenuInputButton
                        {...getToggleButtonProps(
                            { ...getDropdownProps({ preventKeyAction: isOpen }) })}
                        isOpen={isOpen} triggerType={triggerType}>
                        {children}
                    </ToggleMenuInputButton>
                </InputContainer>
                {isOpen && (
                    <MenuOverlayList {...getMenuProps()} className={molclassname} style={menuOverlayStyle}>
                        {
                            grouped.map((its: any, idx: number) => {
                                return (
                                    <CollapsibleHeader
                                        isVisible={true}
                                        searchFilteredItems={searchFilteredItems}
                                        label={its?.[0]?.department_name ?? 'No Department'}
                                        items={its}
                                        isChecked={its.some(it => selectedItems.some(item => item.value === it.value))}
                                        selectSource={items}
                                        groupdedItems={grouped}
                                        moliclassname={moliclassname}
                                        style={menuOverlayListItemStyle}
                                        key={`${its?.length}${idx}`}
                                        highlightedIndex={highlightedIndex}
                                        selectedItems={selectedItems}
                                        getItemProps={getItemProps}
                                        setSelectedItems={setSelectedItems}
                                    />
                                );
                            })}
                    </MenuOverlayList>
                )}
                {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </div>
        </div>
    );
};

export type LabelProps = { hasMandatoryIndicator?: boolean } & ComponentProps<'label'>;
export const Label = (props: LabelProps) => {
    const { hasMandatoryIndicator, children, className, style, ...rest } = props;

    return (
        <label className={classNames(cls.customLabel, className)} style={style} {...rest}>
            {children}
            {hasMandatoryIndicator && <span style={{ color: 'var(--app-color-error)' }}>*</span>}
        </label>
    );
};
export type TagProps = { hasCloseButton?: boolean } & ComponentProps<'div'>;
export const Tag = forwardRef<HTMLDivElement, TagProps>((props, ref) => {
    const { children, style = {}, className = '', hasCloseButton, ...rest } = props;

    return (
        <div ref={ref} tabIndex={hasCloseButton ? 0 : undefined}
            className={classNames(cls.hostCustomTagDefault, className)} style={style} {...rest}>
            <span className={cls.hostCustomTagDefaultText}>{children}</span>
            {hasCloseButton && <CloseIconPurple className={cls.customTagCloseIcon} />}
        </div>
    );
});
export const ErrorMessage = (props: ComponentProps<'p'>) => {
    return <p className={cls.customErrorMessage} {...props} />;
};
export type ToggleMenuInputButtonProps = { isOpen: boolean; triggerType?: 'chevron' | 'downArrow' | 'eyeglassGrey' } & ComponentProps<'button'>;
export const ToggleMenuInputButton = forwardRef<HTMLButtonElement, ToggleMenuInputButtonProps>((props, ref) => {
    const { children, isOpen, className, triggerType, ...rest } = props;

    return (
        <>
            {triggerType ? (
                <button ref={ref} type="button" aria-label="toggle-menu"
                    className={classNames(cls.customToggleMenuInputButton, className)}
                    app-variant={isOpen ? 'open' : 'closed'} app-icon={triggerType ? triggerType : ''} {...rest}>
                    {children}
                    {triggerType === 'eyeglassGrey' && <SearchIconGrey className={cls.customToggleMenuInputButtonIcon} />}
                    {triggerType === 'chevron' && <VectorDown className={cls.customToggleMenuInputButtonIcon} />}
                    {triggerType === 'downArrow' && <DropDownArrow className={cls.customToggleMenuInputButtonIcon} />}
                </button>
            ) :
                children
            }
        </>
    );
});
export const InputContainer = forwardRef<HTMLDivElement, { triggerType?: string; isActive: boolean; hasError: boolean } & ComponentProps<'div'>>((props, ref) => {
    const { isActive, hasError, className, style, triggerType, ...rest } = props;

    return <div ref={ref} {...rest} className={classNames(cls.customInputContainer, className)} style={{ ...style }} app-trigger-type={triggerType} app-state={isActive ? 'active' : hasError ? 'error' : ''} />;
});
export const Input = forwardRef<HTMLInputElement, ComponentProps<'input'>>((props, ref) => {
    const { className, ...rest } = props;

    return <input ref={ref} className={classNames(cls.customInput, className)} {...rest} />;
});
export const MenuOverlayList = forwardRef<HTMLUListElement, ComponentProps<'ul'>>((props, ref) => {
    const { className, style, ...rest } = props;

    return <ul ref={ref} {...rest} className={classNames(cls.customMenuOverlayList, className)} style={{ ...style }} />;
});
export type MenuOverlayListItemProps = {
    isVisible: boolean;
    isHighlighted?: boolean;
    isSelected?: boolean;
    itemType?: 'checkboxes' | 'normal';
} & ComponentProps<'li'>;
export const MenuOverlayListItem = forwardRef<HTMLLIElement, MenuOverlayListItemProps>((props, ref) => {
    const { itemType, children, className, isSelected, isHighlighted, isVisible, style, ...rest } = props;

    if (!isVisible) return null;

    return (
        <li ref={ref} className={classNames(cls.customMenuOverlayListItem, className)}
            app-variant={isSelected ? 'selected' : isHighlighted ? 'highlighted' : ''}
            app-item-type={itemType ? itemType : ''} app-item-display={itemType ? itemType : ''} style={style} {...rest}>
            {itemType === 'checkboxes' && (
                <span>
                    <div className={classNames(cls.customMenuOverlayListItemCheckbox)}>
                        <CustomCheckbox
                            defaultChecked={isSelected}
                            labelStyle={{ fontWeight: 300, fontSize: '16px' }}
                            mainContainerStyle={{ margin: '0 0 0 0' }}
                            checkMarkStyle={{ width: '16px', height: '16px' }}
                            checkIconStyle={{ width: '11px', height: '8px' }}
                            {...rest}
                        />
                    </div>
                </span>
            )}
            <span className={cls.customMenuOverlayListItemChild}>{children}</span>
            {itemType === 'normal' && <span>{isSelected ? <Checked className={cls.customMenuOverlayListItemNormalCheckedMark} />
                :
                <PlusIconPurple className={cls.customMenuOverlayListItemNormalCheckedMark} />}</span>}
        </li>
    );
});

const CollapsibleHeader = (props: SimpleItem & any) => {
    const { label, setSelectedItems, items, selectSource, isVisible, isChecked,
        moliclassname, menuOverlayListItemStyle, highlightedIndex, selectedItems, getItemProps } = props;
    const [open, setOpen] = useState(isChecked);

    return (
        <div className={cls.collapsibleHeaderContainer} app-variant={open ? 'open' : 'closed'} app-display={isVisible ? 'flex' : 'none'}>
            <div
                className={cls.collapsibleHeaderButton}
                onClick={() => {
                    setOpen(!open);
                }}
            >
                <span>
                    <div className={classNames(cls.customMenuOverlayListItemCheckbox)}>
                        <CustomCheckbox
                            defaultChecked={isChecked}
                            labelStyle={{ fontWeight: 300, fontSize: '16px' }}
                            mainContainerStyle={{ margin: '0 0 0 0' }}
                            checkMarkStyle={{ width: '16px', height: '16px' }}
                            checkIconStyle={{ width: '11px', height: '8px' }}
                            onChange={() => {

                                if (isChecked) {
                                    const updatedSelectedItems = selectedItems.filter(selectedItem =>
                                        !items.some(item => item.value === selectedItem.value)
                                    );

                                    setSelectedItems(updatedSelectedItems);
                                    setOpen(false);
                                } else {
                                    setSelectedItems(prev => [...prev, ...items]);
                                    setOpen(true);
                                }
                            }}
                        />
                    </div>
                </span>
                <span className={classNames(cls.collapsibleHeaderText, { [cls.hightlightItem]: isChecked })}>{label}</span>
                <VectorDown className={cls.collapsibleHeaderChevron} />
            </div>
            <div className={cls.collapsibleMenuListContainer}>
                {open &&
                    items?.map((item: any) => {
                        const itemIndex = selectSource?.findIndex((e: any) => e?.value === item?.value);

                        return (
                            <MenuOverlayListItem
                                itemType={'checkboxes'}
                                isVisible={true}
                                className={moliclassname}
                                style={menuOverlayListItemStyle}
                                key={`${item?.value}${selectSource?.indexOf(item)}`}
                                isHighlighted={highlightedIndex === itemIndex}
                                isSelected={selectedItems?.findIndex(e => e.value === item.value) > -1}
                                {...getItemProps({ item, index: itemIndex })}
                            >
                                {item.label}
                            </MenuOverlayListItem>
                        );
                    })}
            </div>

        </div>
    );
};

export default MultiSelectWithCheckboxesAndNestedItems;
